import {
    DateTimeMode,
    getDateRangeFromMode,
    SortOption,
} from './query-filters';
import { PromptAttachmentType } from '@/types/prompt';
import type { QueryParams } from '@/hooks/useQuery';
import { SourceType, type FullSource } from '@/types/source';
import {
    splitSourceSelection,
    type SelectedFolders,
} from '@/stores/sources.store';

export const URL_PARAMS = {
    QUERY: 'q',
    SOURCES: 'sources',
    EXCLUDED: 'excluded',
    DATE_TIME: 'date_time',
    SORT: 'sort',
    CONFIDENCE: 'confidence',
    ATTACHMENT: 'attachment',
    OBJECT_TYPES: 'object_types',
    SOURCE_FOLDERS: 'source_folders',
    SCENE_ONLY: 'scene_only',
} as const;

export const SOURCE_MODE = {
    ALL: 'all',
    SELECTED: 'selected',
} as const;

export const DateTimeModeUrl = {
    [DateTimeMode.ALL]: 'all',
    [DateTimeMode.TODAY]: 'today',
    [DateTimeMode.YESTERDAY]: 'yesterday',
    [DateTimeMode.LAST_HOUR]: 'last_hour',
    [DateTimeMode.LAST_3_HOURS]: 'last_3_hours',
    [DateTimeMode.LAST_6_HOURS]: 'last_6_hours',
    [DateTimeMode.LAST_12_HOURS]: 'last_12_hours',
    [DateTimeMode.LAST_24_HOURS]: 'last_24_hours',
    [DateTimeMode.LAST_3_DAYS]: 'last_3_days',
    [DateTimeMode.LAST_7_DAYS]: 'last_7_days',
    [DateTimeMode.LAST_14_DAYS]: 'last_14_days',
    [DateTimeMode.LAST_30_DAYS]: 'last_30_days',
    [DateTimeMode.CUSTOM]: 'custom',
} as const;

export const SortOptionUrl = {
    [SortOption.DATE_NEWEST]: 'date_newest',
    [SortOption.DATE_OLDEST]: 'date_oldest',
    [SortOption.VIDEO_TIME_NEWEST]: 'video_time_newest',
    [SortOption.VIDEO_TIME_OLDEST]: 'video_time_oldest',
    [SortOption.CONFIDENCE]: 'confidence',
    [SortOption.SOURCE_A_Z]: 'source_a_z',
    [SortOption.SOURCE_Z_A]: 'source_z_a',
} as const;

const DELIMITER = {
    LIST: ',',
    RANGE: '_',
} as const;

const UrlToDateTimeMode = Object.entries(DateTimeModeUrl).reduce(
    (acc, [key, value]) => ({ ...acc, [value]: key as DateTimeMode }),
    {} as Record<string, DateTimeMode>,
);

const UrlToSortOption = Object.entries(SortOptionUrl).reduce(
    (acc, [key, value]) => ({ ...acc, [value]: key as SortOption }),
    {} as Record<string, SortOption>,
);

export type SourceMode = typeof SOURCE_MODE.ALL | typeof SOURCE_MODE.SELECTED;

export interface UrlQueryState {
    query: string;
    sources: {
        mode: SourceMode;
        ids: string[];
        folders: string[];
        excluded: string[];
    };
    dateTime: {
        mode: DateTimeMode;
        customRange?: { startDate: Date; endDate: Date };
    };
    sort?: SortOption;
    confidence: {
        enabled: boolean;
        min: number;
        max: number;
    };
    objectTypes?: number[];
    sceneOnly?: boolean;
    attachment?: {
        url: string;
        type?: PromptAttachmentType;
        filename: string;
    };
}

function encodeSourceIds(ids: string[]): string {
    return ids.map((id) => encodeURIComponent(id)).join(DELIMITER.LIST);
}

function encodeDateRange(startDate: Date, endDate: Date): string {
    return `${startDate.toISOString()}${DELIMITER.LIST}${endDate.toISOString()}`;
}

function encodeConfidenceRange(min: number, max: number): string {
    return `${min}${DELIMITER.RANGE}${max}`;
}

function encodeObjectTypes(classIds: number[]): string {
    return classIds.map((id) => id.toString()).join(DELIMITER.LIST);
}

function decodeObjectTypes(encoded: string): number[] {
    return encoded
        .split(DELIMITER.LIST)
        .filter(Boolean)
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));
}

export function decodeSourceIds(encoded: string): string[] {
    return encoded
        .split(DELIMITER.LIST)
        .filter(Boolean)
        .map((id) => decodeURIComponent(id));
}

function decodeDateRange(
    encoded: string,
): { startDate: Date; endDate: Date } | null {
    const parts = encoded.split(DELIMITER.LIST);
    const [startString, endString] = parts;
    const startDate = new Date(startString);
    const endDate = new Date(endString);
    // An encoded range is exactly two ISO strings, and those contain no commas,
    // so any other count is malformed. new Date() does not throw on a bad
    // string either — it returns an Invalid Date — so the value is checked.
    if (
        parts.length !== 2 ||
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime())
    ) {
        console.warn('Invalid date range in URL:', encoded);
        return null;
    }
    return { startDate, endDate };
}

function decodeConfidenceRange(
    encoded: string,
): { min: number; max: number } | null {
    const parts = encoded.split(DELIMITER.RANGE);
    if (parts.length !== 2) return null;

    const [minStr, maxStr] = parts;
    const min = parseInt(minStr, 10);
    const max = parseInt(maxStr, 10);

    if (isNaN(min) || isNaN(max) || min < 0 || max > 100 || min > max) {
        console.warn('Invalid confidence range in URL:', encoded);
        return null;
    }

    return { min, max };
}

const TRUSTED_ATTACHMENT_PROTOCOLS = new Set(['https:', 'http:', 'blob:']);

function isTrustedAttachmentUrl(url: unknown): boolean {
    if (typeof url !== 'string' || !url) return false;
    try {
        const parsed = new URL(url, window.location.origin);
        return TRUSTED_ATTACHMENT_PROTOCOLS.has(parsed.protocol);
    } catch {
        return false;
    }
}

export function buildUrlStateFromStores(
    query: string,
    sources: {
        all: boolean;
        selected: Set<string>;
        selectedFolders: SelectedFolders;
        excluded: Set<string>;
    },
    filters: {
        dateTime: {
            mode: DateTimeMode;
            customRange?: { startDate: Date; endDate: Date };
        };
        confidence: {
            enabled: boolean;
            min: number;
            max: number;
        };
        sceneOnly?: boolean;
    },
    sortOption?: SortOption,
    attachment?: {
        url: string;
        type?: PromptAttachmentType;
        filename: string;
    },
    objectTypes?: number[],
): UrlQueryState {
    const { folderIds, looseSourceIds } = splitSourceSelection(
        sources.selected,
        sources.selectedFolders,
    );
    return {
        query,
        sources: {
            mode: sources.all ? SOURCE_MODE.ALL : SOURCE_MODE.SELECTED,
            ids: looseSourceIds,
            folders: folderIds,
            excluded: Array.from(sources.excluded),
        },
        dateTime: {
            mode: filters.dateTime.mode,
            customRange: filters.dateTime.customRange,
        },
        ...(sortOption !== undefined && { sort: sortOption }),
        confidence: {
            enabled: filters.confidence.enabled,
            min: filters.confidence.min,
            max: filters.confidence.max,
        },
        ...(objectTypes?.length && { objectTypes }),
        ...(filters.sceneOnly && { sceneOnly: true }),
        attachment,
    };
}

const KNOWN_URL_PARAMS: Set<string> = new Set([
    ...Object.values(URL_PARAMS),
    'startTime',
    'endTime',
]);

export function serializeQueryStateParams(
    searchParams: URLSearchParams,
): string {
    const params = new URLSearchParams();
    searchParams.forEach((value, key) => {
        if (KNOWN_URL_PARAMS.has(key)) {
            params.append(key, value);
        }
    });
    return params.toString();
}

export function serializeToUrlParams(
    state: UrlQueryState,
    existingParams: URLSearchParams,
): URLSearchParams {
    const params = new URLSearchParams();

    existingParams.forEach((value, key) => {
        if (!KNOWN_URL_PARAMS.has(key)) {
            params.set(key, value);
        }
    });

    if (state.query) {
        params.set(URL_PARAMS.QUERY, state.query);
    }

    if (state.sources.mode === SOURCE_MODE.ALL) {
        params.set(URL_PARAMS.SOURCES, SOURCE_MODE.ALL);
        if (state.sources.excluded.length > 0) {
            params.set(
                URL_PARAMS.EXCLUDED,
                encodeSourceIds(state.sources.excluded),
            );
        }
    } else {
        if (state.sources.ids.length > 0) {
            params.set(URL_PARAMS.SOURCES, encodeSourceIds(state.sources.ids));
        }
        if (state.sources.folders.length > 0) {
            params.set(
                URL_PARAMS.SOURCE_FOLDERS,
                encodeSourceIds(state.sources.folders),
            );
        }
    }

    if (
        state.dateTime.mode === DateTimeMode.CUSTOM &&
        state.dateTime.customRange
    ) {
        const { startDate, endDate } = state.dateTime.customRange;
        params.set(URL_PARAMS.DATE_TIME, encodeDateRange(startDate, endDate));
    } else if (state.dateTime.mode !== DateTimeMode.ALL) {
        params.set(URL_PARAMS.DATE_TIME, DateTimeModeUrl[state.dateTime.mode]);
    }

    if (state.sort !== undefined) {
        params.set(URL_PARAMS.SORT, SortOptionUrl[state.sort]);
    }

    if (state.confidence.enabled) {
        params.set(
            URL_PARAMS.CONFIDENCE,
            encodeConfidenceRange(state.confidence.min, state.confidence.max),
        );
    }

    if (state.objectTypes && state.objectTypes.length > 0) {
        params.set(
            URL_PARAMS.OBJECT_TYPES,
            encodeObjectTypes(state.objectTypes),
        );
    }

    if (state.attachment) {
        params.set(URL_PARAMS.ATTACHMENT, JSON.stringify(state.attachment));
    }

    if (state.sceneOnly) {
        params.set(URL_PARAMS.SCENE_ONLY, 'true');
    }

    return params;
}

export function deserializeFromUrlParams(
    searchParams: URLSearchParams,
    earliestStart?: Date,
): Partial<UrlQueryState> {
    const state: Partial<UrlQueryState> = {};

    const query = searchParams.get(URL_PARAMS.QUERY);
    if (query) {
        state.query = query;
    }

    const sourcesParam = searchParams.get(URL_PARAMS.SOURCES);
    const sourceFoldersParam = searchParams.get(URL_PARAMS.SOURCE_FOLDERS);
    const excludedParam = searchParams.get(URL_PARAMS.EXCLUDED);

    if (sourcesParam === SOURCE_MODE.ALL) {
        state.sources = {
            mode: SOURCE_MODE.ALL,
            ids: [],
            folders: [],
            excluded: excludedParam ? decodeSourceIds(excludedParam) : [],
        };
    } else if (sourcesParam || sourceFoldersParam) {
        state.sources = {
            mode: SOURCE_MODE.SELECTED,
            ids: sourcesParam ? decodeSourceIds(sourcesParam) : [],
            folders: sourceFoldersParam
                ? decodeSourceIds(sourceFoldersParam)
                : [],
            excluded: [],
        };
    }

    const dateTimeParam = searchParams.get(URL_PARAMS.DATE_TIME);
    if (dateTimeParam) {
        if (dateTimeParam.includes(DELIMITER.LIST)) {
            const dateRange = decodeDateRange(dateTimeParam);
            if (dateRange) {
                state.dateTime = {
                    mode: DateTimeMode.CUSTOM,
                    customRange: dateRange,
                };
            }
        } else {
            const mode = UrlToDateTimeMode[dateTimeParam];
            if (mode) {
                state.dateTime = { mode };
            } else {
                console.warn('Invalid date_time mode in URL:', dateTimeParam);
            }
        }
    }

    if (!state.dateTime) {
        const startTimeParam = searchParams.get('startTime');
        const endTimeParam = searchParams.get('endTime');
        if (startTimeParam && endTimeParam) {
            const startSeconds = parseTimestampParam(startTimeParam);
            const endSeconds = parseTimestampParam(endTimeParam);
            if (startSeconds !== null && endSeconds !== null) {
                state.dateTime = {
                    mode: DateTimeMode.CUSTOM,
                    customRange: {
                        startDate: new Date(startSeconds * 1000),
                        endDate: new Date(endSeconds * 1000),
                    },
                };
            }
        }
    }

    if (
        earliestStart &&
        state.dateTime?.mode === DateTimeMode.CUSTOM &&
        state.dateTime.customRange &&
        state.dateTime.customRange.startDate < earliestStart
    ) {
        state.dateTime = {
            mode: DateTimeMode.CUSTOM,
            customRange: {
                ...state.dateTime.customRange,
                startDate: earliestStart,
            },
        };
    }

    const sortParam = searchParams.get(URL_PARAMS.SORT);
    if (sortParam) {
        let mappedSortParam = sortParam;
        if (sortParam === 'video_time_newest') {
            mappedSortParam = 'date_newest';
        } else if (sortParam === 'video_time_oldest') {
            mappedSortParam = 'date_oldest';
        }
        const sortOption = UrlToSortOption[mappedSortParam];
        if (sortOption) {
            state.sort = sortOption;
        } else {
            console.warn('Invalid sort option in URL:', sortParam);
        }
    }

    const confidenceParam = searchParams.get(URL_PARAMS.CONFIDENCE);
    if (confidenceParam) {
        const range = decodeConfidenceRange(confidenceParam);
        if (range) {
            state.confidence = {
                enabled: true,
                ...range,
            };
        }
    }

    const objectTypesParam = searchParams.get(URL_PARAMS.OBJECT_TYPES);
    if (objectTypesParam) {
        state.objectTypes = decodeObjectTypes(objectTypesParam);
    }

    const attachmentParam = searchParams.get(URL_PARAMS.ATTACHMENT);
    if (attachmentParam) {
        try {
            const attachment = JSON.parse(attachmentParam);
            if (isTrustedAttachmentUrl(attachment?.url)) {
                state.attachment = attachment;
            } else {
                console.warn('Untrusted attachment URL:', attachment?.url);
            }
        } catch (error) {
            console.warn('Invalid attachment in URL:', attachmentParam);
        }
    }

    if (searchParams.get(URL_PARAMS.SCENE_ONLY) === 'true') {
        state.sceneOnly = true;
    }

    return state;
}

export function parseTimestampParam(value: string): number | null {
    const seconds = Number(value);
    if (Number.isNaN(seconds) || seconds <= 0) return null;
    return seconds;
}

export function isValidDateTimeMode(
    value: string,
): value is keyof typeof UrlToDateTimeMode {
    return value in UrlToDateTimeMode;
}

export function isValidSortOption(
    value: string,
): value is keyof typeof UrlToSortOption {
    return value in UrlToSortOption;
}

export function getSortFromSearchParams(
    searchParams: URLSearchParams,
    defaultSort: SortOption,
): SortOption {
    const sortParam = searchParams.get(URL_PARAMS.SORT);
    if (!sortParam) return defaultSort;
    const mapped =
        sortParam === 'video_time_newest'
            ? 'date_newest'
            : sortParam === 'video_time_oldest'
              ? 'date_oldest'
              : sortParam;
    const option = UrlToSortOption[mapped as keyof typeof UrlToSortOption];
    return option ?? defaultSort;
}

export function setSortInSearchParams(
    searchParams: URLSearchParams,
    sortOption: SortOption,
    defaultSort: SortOption,
): URLSearchParams {
    const next = new URLSearchParams(searchParams);
    if (sortOption === defaultSort) {
        next.delete(URL_PARAMS.SORT);
    } else {
        next.set(URL_PARAMS.SORT, SortOptionUrl[sortOption]);
    }
    return next;
}

function getDateRangeFromUrlState(
    dateTime: UrlQueryState['dateTime'] | undefined,
): { startDate: Date; endDate: Date } | null {
    if (!dateTime) return null;
    if (dateTime.mode === DateTimeMode.CUSTOM && dateTime.customRange) {
        return dateTime.customRange;
    }
    return getDateRangeFromMode(dateTime.mode);
}

function getSourceIdsFromUrlState(
    sources: UrlQueryState['sources'] | undefined,
    allSources: FullSource[],
    folderMembers: SelectedFolders,
): string[] {
    if (!sources) return [];
    if (sources.mode === SOURCE_MODE.ALL) {
        if (sources.excluded.length === 0) return [];
        const excludedSet = new Set(sources.excluded);
        return allSources
            .filter((s) => !excludedSet.has(s.id))
            .map((s) => s.id);
    }
    const ids = new Set(sources.ids);
    sources.folders.forEach((folderId) => {
        (folderMembers.get(folderId) ?? []).forEach((memberId) =>
            ids.add(memberId),
        );
    });
    return Array.from(ids);
}

export function buildQueryParamsFromUrlState(
    urlState: Partial<UrlQueryState>,
    demo: boolean,
    allSources: FullSource[],
    folderMembers: SelectedFolders,
): QueryParams | null {
    const sourceIds = getSourceIdsFromUrlState(
        urlState.sources,
        allSources,
        folderMembers,
    );
    const explicitSelection =
        urlState.sources?.mode === SOURCE_MODE.SELECTED &&
        (urlState.sources.ids.length > 0 ||
            urlState.sources.folders.length > 0);
    if (explicitSelection && sourceIds.length === 0) {
        return null;
    }
    const dateRange = getDateRangeFromUrlState(urlState.dateTime);
    const videoSourceSelected =
        urlState.sources?.mode === SOURCE_MODE.SELECTED &&
        sourceIds.some(
            (id) =>
                allSources.find((source) => source.id === id)?.type ===
                SourceType.FILE,
        );

    const minConfidence = urlState.confidence?.enabled
        ? urlState.confidence.min
        : undefined;

    const attachment = urlState.attachment
        ? { url: urlState.attachment.url, type: urlState.attachment.type }
        : undefined;

    return {
        text: urlState.query || '',
        sourceIds,
        filters:
            dateRange && !videoSourceSelected
                ? { startTime: dateRange.startDate, endTime: dateRange.endDate }
                : undefined,
        attachment,
        ...(urlState.objectTypes?.length && {
            classIds: urlState.objectTypes,
        }),
        demo,
        minConfidence,
        ...(urlState.sceneOnly && { sceneOnly: true }),
    };
}
